import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarClock, FileDown, Loader2, Mail, MessageCircle, RefreshCw, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DeadlineReminderDialog } from "@/components/DeadlineReminderDialog";
import { CALL_OUTCOMES, CallOutcomeDialog } from "@/components/CallOutcomeDialog";
import { api, formatApiError, formatCurrency, formatDate } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const IMMINENT_DAYS = 7;
const PERIODS = [
  { k: "all", l: "Tutte" },
  { k: "week", l: "Questa settimana" },
  { k: "month", l: "Questo mese" },
  { k: "30", l: "Prossimi 30 gg" },
  { k: "60", l: "Prossimi 60 gg" },
  { k: "90", l: "Prossimi 90 gg" },
  { k: "custom", l: "Personalizzato" },
];
const STATUS_LBL = { in_scadenza: "In scadenza", scaduta: "Scaduta / arretrata", rinnovata: "Rinnovata" };
const TYPE_LBL = { auto: "Auto", moto: "Moto", ass_stradale: "Assistenza stradale", casa: "Casa", vita: "Vita", infortuni: "Infortuni", rc: "RC" };
const iso = (d) => d.toISOString().slice(0, 10);

function periodRange(k) {
  const t = new Date(); t.setHours(12, 0, 0, 0);
  if (k === "week") {
    const day = (t.getDay() + 6) % 7;
    const mon = new Date(t); mon.setDate(t.getDate() - day);
    const sun = new Date(mon); sun.setDate(mon.getDate() + 6);
    return [iso(mon), iso(sun)];
  }
  if (k === "month") return [iso(new Date(t.getFullYear(), t.getMonth(), 1, 12)), iso(new Date(t.getFullYear(), t.getMonth() + 1, 0, 12))];
  if (["30", "60", "90"].includes(k)) { const e = new Date(t); e.setDate(t.getDate() + Number(k)); return [iso(t), iso(e)]; }
  return [null, null];
}

function rowTone(r) {
  if (r.category === "scaduta") return { row: "bg-red-50 hover:bg-red-100 border-l-4 border-l-red-500", badge: "bg-red-600 text-white" };
  if (r.category === "rinnovata") return { row: "bg-white hover:bg-slate-50 border-l-4 border-l-emerald-400 opacity-80", badge: "bg-emerald-600 text-white" };
  if (r.days_left <= IMMINENT_DAYS) return { row: "bg-orange-50 hover:bg-orange-100 border-l-4 border-l-orange-500", badge: "bg-orange-500 text-white" };
  if (r.days_left <= 30) return { row: "bg-yellow-50/70 hover:bg-yellow-100 border-l-4 border-l-yellow-400", badge: "bg-yellow-400 text-yellow-950" };
  return { row: "bg-white hover:bg-slate-50 border-l-4 border-l-transparent", badge: "bg-slate-200 text-slate-700" };
}

function daysLabel(r) {
  if (r.category === "rinnovata") return "Rinnovata";
  if (r.days_left < 0) return `Scaduta da ${-r.days_left} gg`;
  if (r.days_left === 0) return "Scade oggi";
  return `tra ${r.days_left} gg`;
}

function Stat({ label, value, cls, testid }) {
  return (
    <div className={`rounded-lg border px-4 py-2 ${cls}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider font-semibold opacity-80">{label}</div>
      <div className="text-2xl font-extrabold tabular-nums">{value}</div>
    </div>
  );
}

export default function PolicyDeadlines() {
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [company, setCompany] = useState("all");
  const [ptype, setPtype] = useState("all");
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");

  const [reminder, setReminder] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [callRow, setCallRow] = useState(null);
  const closeCall = useCallback(() => setCallRow(null), []);
  const onCallSaved = useCallback((id, log, st) => setData((d) => ({
    ...d,
    items: st === "disdetta" ? d.items.filter((r) => r.id !== id) : d.items.map((r) => (r.id === id ? { ...r, last_call: log } : r)),
  })), []);
  const closeReminder = useCallback(() => setReminder(null), []);
  const onReminderSent = useCallback((id, rec) => setData((d) => ({ ...d, items: d.items.map((r) => (r.id === id ? { ...r, last_reminder: rec } : r)) })), []);

  const buildParams = () => {
    const [f, t] = period === "custom" ? [from || null, to || null] : periodRange(period);
    const params = {};
    if (f) params.date_from = f;
    if (t) params.date_to = t;
    if (company !== "all") params.company = company;
    if (ptype !== "all") params.type = ptype;
    if (status !== "all") params.status = status;
    return params;
  };

  const exportPdf = async () => {
    setExporting(true);
    try {
      const params = { ...buildParams(), period_label: PERIODS.find((p) => p.k === period)?.l };
      if (q.trim()) params.q = q.trim();
      const { data: blob } = await api.get("/policies/deadlines/pdf", { params, responseType: "blob" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `scadenziario_${new Date().toISOString().slice(0, 10)}.pdf`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setExporting(false); }
  };

  useEffect(() => {
    const params = buildParams();
    setData((d) => (d ? { ...d, loading: true } : null));
    api.get("/policies/deadlines", { params })
      .then(({ data: r }) => setData(r))
      .catch((e) => { toast.error(formatApiError(e)); setData({ items: [], companies: [], types: [] }); });
  }, [period, from, to, company, ptype, status]); // eslint-disable-line react-hooks/exhaustive-deps

  const items = useMemo(() => {
    const s = q.trim().toLowerCase();
    const arr = data?.items || [];
    if (!s) return arr;
    return arr.filter((r) => [r.client_name, r.policy_number, r.vehicle_plate, r.company].some((x) => (x || "").toLowerCase().includes(s)));
  }, [data, q]);
  const stats = useMemo(() => ({
    overdue: items.filter((r) => r.category === "scaduta").length,
    imminent: items.filter((r) => r.category === "in_scadenza" && r.days_left <= IMMINENT_DAYS).length,
    month: items.filter((r) => r.category === "in_scadenza" && r.days_left > IMMINENT_DAYS && r.days_left <= 30).length,
    renewed: items.filter((r) => r.category === "rinnovata").length,
  }), [items]);

  return (
    <div className="space-y-5" data-testid="policy-deadlines-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight flex items-center gap-3">
            <CalendarClock className="h-9 w-9 text-orange-500" /> Scadenziario Polizze
          </h1>
          <p className="text-sm text-slate-500 mt-1">Polizze ordinate per scadenza effettiva (prossima rata non pagata o fine polizza).</p>
        </div>
        <Button onClick={exportPdf} disabled={exporting || !items.length} variant="outline" className="gap-2" data-testid="deadlines-export-pdf">
          {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />} Scarica PDF elenco
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Arretrate" value={stats.overdue} cls="border-red-200 bg-red-50 text-red-800" testid="deadlines-stat-overdue" />
        <Stat label={`Entro ${IMMINENT_DAYS} giorni`} value={stats.imminent} cls="border-orange-200 bg-orange-50 text-orange-800" testid="deadlines-stat-imminent" />
        <Stat label="Entro 30 giorni" value={stats.month} cls="border-yellow-200 bg-yellow-50 text-yellow-900" testid="deadlines-stat-month" />
        <Stat label="Rinnovate" value={stats.renewed} cls="border-emerald-200 bg-emerald-50 text-emerald-800" testid="deadlines-stat-renewed" />
      </div>

      <div className="rounded-lg border border-slate-200 bg-white p-3 space-y-3" data-testid="deadlines-filters">
        <div className="flex flex-wrap gap-1.5">
          {PERIODS.map((p) => (
            <button key={p.k} type="button" onClick={() => setPeriod(p.k)} data-testid={`deadlines-period-${p.k}`}
              className={`h-8 px-3 rounded-full text-xs font-medium border transition-colors ${period === p.k ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"}`}>
              {p.l}
            </button>
          ))}
          {period === "custom" && (
            <span className="inline-flex items-center gap-1.5 text-xs">
              dal <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-8 w-40" data-testid="deadlines-date-from" />
              al <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-8 w-40" data-testid="deadlines-date-to" />
            </span>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
          <Select value={company} onValueChange={setCompany}>
            <SelectTrigger className="h-9" data-testid="deadlines-filter-company"><SelectValue placeholder="Compagnia" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutte le compagnie</SelectItem>
              {(data?.companies || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={ptype} onValueChange={setPtype}>
            <SelectTrigger className="h-9" data-testid="deadlines-filter-type"><SelectValue placeholder="Tipo polizza" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti i tipi</SelectItem>
              {(data?.types || []).map((t) => <SelectItem key={t} value={t}>{TYPE_LBL[t] || t}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="h-9" data-testid="deadlines-filter-status"><SelectValue placeholder="Stato" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti gli stati</SelectItem>
              {Object.entries(STATUS_LBL).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="relative">
            <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca cliente, polizza, targa…" className="h-9 pl-8" data-testid="deadlines-search" />
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-slate-200 overflow-x-auto bg-white">
        <table className="w-full text-sm" data-testid="deadlines-table">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
            <tr>
              <th className="text-left p-2.5">Scadenza</th>
              <th className="text-left p-2.5">Cliente</th>
              <th className="text-left p-2.5">Polizza</th>
              <th className="text-left p-2.5">Compagnia</th>
              <th className="text-left p-2.5">Tipo</th>
              <th className="text-left p-2.5">Veicolo</th>
              <th className="text-right p-2.5">Premio</th>
              <th className="text-left p-2.5">Stato</th>
              <th className="text-left p-2.5">Esito chiamata</th>
              <th className="text-right p-2.5">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {!data ? (
              <tr><td colSpan={10} className="p-8 text-center"><Loader2 className="h-6 w-6 animate-spin inline text-slate-400" /></td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={10} className="p-8 text-center text-slate-500" data-testid="deadlines-empty">Nessuna polizza per i filtri selezionati.</td></tr>
            ) : items.map((r) => {
              const tone = rowTone(r);
              return (
                <tr key={r.id} className={`border-t border-slate-100 transition-colors ${tone.row}`} data-testid={`deadline-row-${r.id}`}>
                  <td className="p-2.5 whitespace-nowrap">
                    <div className="font-semibold tabular-nums">{formatDate(r.expiry_date)}</div>
                    <span className={`inline-block mt-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded ${tone.badge}`} data-testid={`deadline-days-${r.id}`}>{daysLabel(r)}</span>
                    {r.is_installment && <div className="text-[10px] text-slate-500 mt-0.5">rata · fine {formatDate(r.end_date)}</div>}
                  </td>
                  <td className="p-2.5">
                    {r.client_id ? (
                      <Link to={`/clients?focus=${r.client_id}`} className="font-semibold text-blue-700 hover:underline" data-testid={`deadline-client-link-${r.id}`}>{r.client_name}</Link>
                    ) : <span>{r.client_name}</span>}
                    {r.client_phone && <div className="text-[11px] text-slate-500">{r.client_phone}</div>}
                  </td>
                  <td className="p-2.5">
                    <Link to={`/policies?focus=${r.id}&return_to=${encodeURIComponent("/policies/deadlines")}`} className="font-mono text-blue-700 hover:underline" data-testid={`deadline-policy-link-${r.id}`}>
                      {r.policy_number || "apri polizza"}
                    </Link>
                    {r.frazionamento && <div className="text-[11px] text-slate-500 capitalize">{r.frazionamento}</div>}
                  </td>
                  <td className="p-2.5">{r.company || "—"}</td>
                  <td className="p-2.5">{TYPE_LBL[r.type] || r.type || "—"}</td>
                  <td className="p-2.5">
                    {r.vehicle_plate ? <span className="font-mono text-xs font-bold border border-slate-400 rounded px-1.5 bg-white">{r.vehicle_plate}</span> : "—"}
                    {r.vehicle && <div className="text-[11px] text-slate-500">{r.vehicle}</div>}
                  </td>
                  <td className="p-2.5 text-right tabular-nums">{r.premium != null ? formatCurrency(r.premium) : "—"}</td>
                  <td className="p-2.5 text-xs">
                    {STATUS_LBL[r.category]}
                    {r.last_reminder && (
                      <div className="text-[10px] text-slate-500 mt-0.5" data-testid={`deadline-last-reminder-${r.id}`}>
                        Avvisato {r.last_reminder.channel === "email" ? "via email" : "via WhatsApp"} il {formatDate(r.last_reminder.at)}
                      </div>
                    )}
                    {r.last_auto_reminder && (
                      <div className="text-[10px] text-slate-500 mt-0.5" data-testid={`deadline-auto-reminder-${r.id}`}>Avviso automatico il {formatDate(r.last_auto_reminder)}</div>
                    )}
                  </td>
                  <td className="p-2.5 text-xs max-w-[220px]">
                    <button type="button" onClick={() => setCallRow(r)} className="text-left w-full group" data-testid={`deadline-call-${r.id}`}>
                      {r.last_call ? (
                        <>
                          <span className={`inline-block px-1.5 py-0.5 rounded border text-[10px] font-bold ${CALL_OUTCOMES[r.last_call.outcome]?.cls || ""}`} data-testid={`deadline-call-outcome-${r.id}`}>{CALL_OUTCOMES[r.last_call.outcome]?.l}</span>
                          <span className="text-[10px] text-slate-500 ml-1">{formatDate(r.last_call.at)}</span>
                          {r.last_call.callback_date && <div className="text-[10px] text-amber-800 font-semibold">Richiamare il {formatDate(r.last_call.callback_date)}</div>}
                          {r.last_call.note && <div className="text-[11px] text-slate-600 truncate">{r.last_call.note}</div>}
                        </>
                      ) : <span className="text-[11px] text-blue-700 group-hover:underline">+ Segna esito</span>}
                    </button>
                  </td>
                  <td className="p-2.5">
                    <div className="flex items-center justify-end gap-1">
                      {r.category !== "rinnovata" && (
                        <Link to={`/policies?focus=${r.id}&action=renew&return_to=${encodeURIComponent("/policies/deadlines")}`} data-testid={`deadline-renew-${r.id}`}
                          className="inline-flex items-center gap-1 h-7 px-2.5 rounded-md bg-slate-900 text-white text-xs font-medium hover:bg-slate-700 transition-colors">
                          <RefreshCw className="h-3.5 w-3.5" /> Rinnova
                        </Link>
                      )}
                      <button type="button" title="Promemoria WhatsApp" onClick={() => setReminder({ row: r, channel: "whatsapp" })} data-testid={`deadline-whatsapp-${r.id}`}
                        className="h-7 w-7 inline-flex items-center justify-center rounded-md border border-emerald-300 text-emerald-700 bg-white hover:bg-emerald-50 transition-colors">
                        <MessageCircle className="h-4 w-4" />
                      </button>
                      <button type="button" title="Promemoria email" onClick={() => setReminder({ row: r, channel: "email" })} data-testid={`deadline-email-${r.id}`}
                        className="h-7 w-7 inline-flex items-center justify-center rounded-md border border-blue-300 text-blue-700 bg-white hover:bg-blue-50 transition-colors">
                        <Mail className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {data && <div className="text-xs text-slate-500" data-testid="deadlines-count">{items.length} polizze</div>}
      <DeadlineReminderDialog target={reminder} onClose={closeReminder} onSent={onReminderSent} />
      <CallOutcomeDialog row={callRow} onClose={closeCall} onSaved={onCallSaved} />
    </div>
  );
}
